import { Button } from "@mui/material";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React from "react";

import { Delete, Edit } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../redux/store";
import { setLoadingAction } from "../../../redux/utils/actions";
import departmentService from "../../../services/department";
import { IUserTableHeadCell } from "../../../types/user/userTable";
import { logger } from "../../../utils/logger";
import SaveButton from "../../common/Button";
import { IDepartment } from "./Department";
import styles from "./styles.module.scss";
import { userStatus } from "../../../constants/user";

const departmentExpireOnLabelMap = {
  [userStatus.SUBMITTED]: "from Submission Time",
  [userStatus.APPROVED]: "from Approval Time",
};

const headCells: readonly IUserTableHeadCell[] = [
  {
    id: "departmentName",
    numeric: false,
    label: "Department Name",
  },
  {
    id: "expiresIn",
    numeric: false,
    label: "Expiry Days",
  },
  {
    id: "maxAmount",
    numeric: false,
    label: "Max Approved Amount (INR)",
  },
  {
    id: "action",
    numeric: true,
    label: "Action",
  },
];

export default function DepartmentListTable({
  gridData,
  setGridData,
  getData,
}: {
  gridData: IDepartment[];
  setGridData: React.Dispatch<React.SetStateAction<IDepartment[]>>;
  getData: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const updateDepartment = async (index: number) => {
    try {
      dispatch(setLoadingAction(true));
      await departmentService.update(gridData[index]._id, {
        name: gridData[index].name,
        expiresIn: gridData[index].expiresIn,
        timeType: gridData[index].timeType,
      });
      getData();
      dispatch(setLoadingAction(false));
    } catch (e) {
      dispatch(setLoadingAction(false));
      logger.error(e);
    }
  };

  const deleteDepartment = async (index: number) => {
    try {
      dispatch(setLoadingAction(true));
      await departmentService.deleteDistrict(gridData[index]._id);
      getData();
      dispatch(setLoadingAction(false));
    } catch (e) {
      dispatch(setLoadingAction(false));
      logger.error(e);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size="medium"
          >
            <TableHead>
              <TableRow>
                {headCells.map((headCell, i) => (
                  <TableCell
                    key={i}
                    align={headCell.id === "action" ? "right" : "left"}
                    className={styles.thead}
                    sx={{
                      ...(headCell.id === "departmentName" && {
                        width: "25%",
                        maxWidth: "25%",
                      }),
                      ...(headCell.id === "expiresIn" && {
                        width: "120px",
                        minWidth: "120px",
                      }),
                      ...(headCell.id === "maxAmount" && {
                        width: "150px",
                        minWidth: "150px",
                      }),
                      ...(headCell.id === "action" && {
                        width: "100px",
                        minWidth: "100px",
                      }),
                    }}
                  >
                    <b className={styles.tableHead}>{headCell.label}</b>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {gridData?.map((data, i) => (
                <TableRow key={`treatment-${i}`}>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      width: "25%",
                      maxWidth: "25%",
                    }}
                    className={styles.tableDefine}
                  >
                    <span
                      style={{
                        color: "#da3923",
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {data.name}
                    </span>
                  </TableCell>

                  <TableCell
                    sx={{
                      width: "150px",
                      minWidth: "150px",
                    }}
                    className={styles.tableDefine}
                  >
                    <span>
                      {data.expiresIn ?? ""}
                      {data.expiresIn &&
                        (data.expiresIn > 1 ? " days " : " day ")}{" "}
                      {data.expiresIn &&
                        departmentExpireOnLabelMap[data.expiresOn]}
                    </span>
                  </TableCell>

                  <TableCell
                    sx={{
                      width: "150px",
                      minWidth: "150px",
                    }}
                    className={styles.tableDefine}
                  >
                    <span>
                      {data.maxAmount ?? ""}
                      {data.maxAmount &&
                        (data.maxAmount > 1 ? " INR " : " INR ")}
                    </span>
                  </TableCell>

                  <TableCell className={styles.tableDefine} align="left">
                    <Box className={styles.actionSx}>
                      {data.isEditOpened ? (
                        <SaveButton
                          color="alert"
                          type="button"
                          onClick={() => updateDepartment(i)}
                          sx={{
                            minWidth: "32px",
                            height: "32px",
                            padding: "4px 8px",
                            marginRight: "8px",
                            fontSize: "14px",
                          }}
                        >
                          Save
                        </SaveButton>
                      ) : (
                        <Button
                          sx={{ minWidth: "auto", p: 0 }}
                          type="button"
                          className={styles.btnLink}
                        >
                          <Edit
                            sx={{ color: "#606164" }}
                            onClick={() => {
                              navigate(`/department/edit/${data._id}`);
                            }}
                          />
                        </Button>
                      )}
                      <Button
                        sx={{ minWidth: "auto", p: 0 }}
                        type="button"
                        className={styles.btnLink}
                        onClick={() => deleteDepartment(i)}
                      >
                        <Delete sx={{ color: "#606164" }} />
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
